import React, { useState } from "react";
import { MdOnlinePrediction, MdOutlineWifiOff, MdCall } from "react-icons/md";
import {
  FaCalendarAlt,
  FaArrowRight,
  FaDownload,
  FaFileInvoice,
  FaMapMarkerAlt,
  FaChevronDown,
  FaChevronUp,
  FaPaw ,FaClock,
  FaDirections,
  FaPhoneAlt,
} from "react-icons/fa";

export const Listview = ({
  bookings = [],
  onViewInvoice,
  onDownloadInvoice,
}) => {
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  const [showPhoneForBooking, setShowPhoneForBooking] = useState(null);

const togglePhoneDisplay = (bookingId) => {
  setShowPhoneForBooking(showPhoneForBooking === bookingId ? null : bookingId);
};

  const getStatusBadge = (status) => {
    const normalized = (status || "active").toLowerCase();
    if (normalized === "completed")
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    if (normalized === "cancelled" || normalized === "cancel")
      return "bg-rose-100 text-rose-700 border-rose-200";
    return "bg-sky-100 text-sky-700 border-sky-200";
  };

  const displayStatus = (status) => {
    if (!status) return "Unknown";

    const normalized = status.trim().toLowerCase();

    if (normalized === "cancel" || normalized === "cancelled") {
      return "Cancelled";
    }

    if (normalized === "no_show") {
      return "No-show";
    }

    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const toggleExpand = (bookingId) => {
    setExpandedBookingId(expandedBookingId === bookingId ? null : bookingId);
  };
  
  const formatAmount = (amount) => {
    const num = Number(amount) || 0;
    return Number.isInteger(num) ? num : num.toFixed(0);
  };

  // Calculate grand total for a booking
  const calculateGrandTotal = (booking) => {
    if (!booking.items || !Array.isArray(booking.items)) return 0;
    
    return booking.items.reduce((sum, item) => {
      // Use grand_total if available, otherwise calculate total_price + additional_amount
      const itemTotal = item.grand_total || 
                       (item.total_price || 0) + (item.additional_amount || 0);
      return sum + itemTotal;
    }, 0);
  };

  const formatDateDisplay = (item) => {
    if (item.type === "boarding" && item.start_date && item.end_date) {
      const startDate = new Date(item.start_date).toLocaleDateString("en-GB");
      const endDate = new Date(item.end_date).toLocaleDateString("en-GB");
      return `${startDate} – ${endDate}`;
    }
    return item.start_date
      ? new Date(item.start_date).toLocaleDateString("en-GB")
      : "--";
  };

// Helper function to format duration display
  //const formatDurationDisplay = (item) => {
   // const duration = item.duration || "";
    
    //if (item.type === "boarding" && duration) {
     // const days = parseInt(duration);
      //if (!isNaN(days)) {
      //  return `${days} Day${days > 1 ? 's' : ''}`;
     // }
   // }
    //return duration;
  //};
  const formatDurationDisplay = (item) => {
  if (!item?.duration) return "";

  const raw = String(item.duration).toLowerCase().trim();

  // BOARDING → show in days
  if (item.type === "boarding") {
    const days = parseInt(raw);
    if (!isNaN(days)) {
      return `${days} day${days > 1 ? "s" : ""}`;
    }
    return raw; // fallback if already "2 days"
  }

  // GROOMING / VET / OTHER → show in mins
  const minutes = parseInt(raw);

  if (!isNaN(minutes)) {
    return `${minutes} mins`;
  }

  // If already contains "minute", normalize
  if (raw.includes("minute")) {
    return raw.replace("minutes", "mins").replace("minute", "min");
  }

  return raw;
};

// Helper function to check if time info should be shown
  const hasTimeInfoDisplay = (item) => {
    if (item.type === "boarding") {
      return !!formatDurationDisplay(item);
    }
    const timeSlot = item.start_time || item.time_range || "";
    return !!(timeSlot || formatDurationDisplay(item));
  };

  const truncateString = (str, maxLength = 20) => {
  if (!str) return '';
  return str.length > maxLength ? `${str.substring(0, maxLength)}...` : str;
};

  // Get Google Maps URL for directions
  const getMapsUrl = (booking) => {
    const officeAddress = booking.items[0]?.vendor_address?.officeAddress;
    const lat = officeAddress?.latitude;
    const lng = officeAddress?.longitude;

    let destination = "Bengaluru, Karnataka";
    if (lat && lng) {
      destination = `${lat},${lng}`;
    }
    const encodedDestination = encodeURIComponent(destination);
    return `https://www.google.com/maps/dir/?api=1&destination=${encodedDestination}&travelmode=driving`;
  };

    
   // Calculate total paid amount
  const calculateTotalPaid = (booking) => {
    if (!booking.items || !Array.isArray(booking.items)) return 0;
    
    return booking.items.reduce((sum, item) => {
      const paidAmount = item.paid_amount || 0;
      return sum + paidAmount;
    }, 0);
  };

  // Calculate total remaining amount
  const calculateTotalRemaining = (booking) => {
    if (!booking.items || !Array.isArray(booking.items)) return 0;
    
    return booking.items.reduce((sum, item) => {
      const remaining = item.remaining_amount ?? 
                       (item.grand_total - (item.paid_amount || 0));
      return sum + (remaining > 0 ? remaining : 0);
    }, 0);
  };

  // Get payment status for the booking
  const getBookingPaymentStatus = (booking) => {
    if (!booking.items || !Array.isArray(booking.items)) return "unpaid";
    
    const allPaid = booking.items.every(item => 
      item.payment_status?.toLowerCase() === "paid"
    );
    if (allPaid) return "paid";
    
    const hasPartial = booking.items.some(item => 
      item.payment_status?.toLowerCase() === "partial"
    );
    if (hasPartial) return "partial";
    
    return "unpaid";
  };




  // Check if all items in the booking are paid
  const isBookingPaid = (booking) => {
    if (!booking.items || !Array.isArray(booking.items)) return false;
    
    return booking.items.every(item => 
      item.payment_status?.toLowerCase() === "paid"
    );
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
      {bookings.map((booking, index) => {
        //const grandTotal = calculateGrandTotal(booking);
        //const isPaid = isBookingPaid(booking);
        const grandTotal = booking.grand_total;
        const totalPaid = calculateTotalPaid(booking);
        const totalRemaining = calculateTotalRemaining(booking);
        const paymentStatus = getBookingPaymentStatus(booking);
        const isPaid = paymentStatus === "paid";
        const isPartial = paymentStatus === "partial";
        const isUnpaid = paymentStatus === "unpaid";
        const mapsUrl = getMapsUrl(booking);

        const bookingId = booking._id || booking.order_number || index;
        const isExpanded = expandedBookingId === bookingId;
        const visibleItems = isExpanded
          ? booking.items
          : booking.items?.slice(0, 3);
        const hasMoreItems = (booking.items?.length || 0) > 3;

        return (
          <div
            key={bookingId}
            className="group bg-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-2xl transition-all duration-300 flex flex-col overflow-hidden h-full"
          >
             {/* Header: Order ID & Type */}
                  <div className="flex flex-wrap items-center justify-between px-5 py-4 bg-gray-50/50 border-b border-gray-100">
                    <div className="flex flex-col">
                      {/*<span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                        Order ID
                      </span>*/}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm font-black text-gray-900 leading-none">
                          #{booking.order_number}
                        </span>
                          {/* Premium Flip Contact Badge */}
                    <div 
                      className="relative group w-7 sm:w-auto sm:min-w-[85px] cursor-pointer"
                      onClick={() => togglePhoneDisplay(booking.order_number)}
                    >
                      <div className={`transition-all duration-500 preserve-3d ${showPhoneForBooking === booking.order_number ? 'rotate-y-180' : ''}`}>
                        {/* Front - Contact Us */}
                        <div className={`backface-hidden w-7 h-7 sm:w-auto sm:h-auto sm:px-2 sm:py-1 bg-gradient-to-r from-green-400 to-emerald-500 text-white rounded-full text-[12px] font-bold flex items-center justify-center sm:justify-start gap-1 shadow-md ${showPhoneForBooking === booking.order_number ? 'hidden' : 'flex'}`}>
                          <FaPhoneAlt size={12} />
                          <span className="hidden sm:inline">Contact Us</span>
                        </div>
                        
                        {/* Back - Phone & Call */}
                        <div className={`backface-hidden rotate-y-180 w-7 h-7 sm:w-auto sm:h-auto sm:px-2 sm:py-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full text-[12px] font-bold flex items-center justify-center sm:justify-start gap-1 shadow-md ${showPhoneForBooking === booking.order_number ? 'flex' : 'hidden'}`}>
                          <MdCall className="hidden sm:block" size={12} />
                          <span className="hidden sm:inline max-w-[80px] truncate">{booking.items[0]?.vendor_phone || 'No phone'}</span>
                          {booking.items[0]?.vendor_phone && (
                            <a
                              href={`tel:${booking.items[0]?.vendor_phone}`}
                              className="w-full h-full sm:w-auto sm:h-auto sm:ml-0.5 sm:px-2 sm:py-0.5 sm:bg-white text-white sm:text-blue-600 rounded-full text-[12px] font-bold hover:bg-gray-100 transition flex items-center justify-center"
                              onClick={(e) => e.stopPropagation()}
                              aria-label="Call vendor"
                            >
                              <MdCall className="sm:hidden" size={12} />
                              <span className="hidden sm:inline">Call</span>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                       <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition text-[10px] font-bold whitespace-nowrap"
                      >
                        <FaDirections size={20} />
                      </a>
                  </div>
                </div>
              <div
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tight border ${
                  booking.order_type === "online"
                    ? "bg-green-50 text-green-700 border-green-100"
                    : "bg-gray-100 text-gray-600 border-gray-200"
                }`}
              >
                {booking.order_type === "online" ? (
                  <>
                    <MdOnlinePrediction size={14} className="animate-pulse" />{" "}
                    
                  </>
                ) : (
                  <>
                    <MdOutlineWifiOff size={14} /> 
                  </>
                )}
              </div>
            </div>

            <div className="px-6 py-1 flex flex-col flex-grow">
              {/* Vendor Info */}
              <div className="flex items-center gap-4 mb-3 w-full overflow-hidden">
                {booking.vendor_info?.logo ? (
                  <img
                    src={booking.vendor_info.logo}
                    alt="Vendor Logo"
                    className="w-12 h-12 flex-shrink-0 rounded-2xl object-cover shadow-lg shadow-orange-100"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className={`w-12 h-12 flex-shrink-0 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 text-white items-center justify-center font-bold text-xl shadow-lg shadow-orange-100 ${
                    booking.vendor_info?.logo ? "hidden" : "flex"
                  }`}
                >
                  {booking.items[0]?.vendor_brand_name?.charAt(0) || "V"}
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 truncate text-lg group-hover:text-orange-600 transition-colors">
                    {booking.items[0]?.vendor_brand_name || "Vendor"}
                  </h3>
                  <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-gray-500">
                    <FaMapMarkerAlt size={14} />
                    <span className="text-xs line-clamp-2 leading-tight">
                      {booking.items[0]?.vendor_address?.officeAddress?.locality ||
                      booking.items[0]?.vendor_address?.homeAddress?.buildingName||
                        "City unavailable"}
                    </span>
                  </div>
                     {/*<a
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition text-[10px] font-bold whitespace-nowrap"
                      >
                        <FaDirections size={10} /> Directions
                      </a>*/}
                 </div> 
                </div>
                {/* RIGHT: Invoice Actions */}
                <div className="ml-auto flex items-center gap-3 flex-shrink-0">                 
                  <button
                    onClick={() => onViewInvoice?.(booking)}
                    className="p-2 rounded-lg border border-gray-200 text-blue-900 hover:bg-gray-50 hover:border-gray-300 transition flex items-center justify-center"
                    title="View Invoice"
                  >
                    <FaFileInvoice size={18} />
                  </button>

                  <button
                    onClick={() => onDownloadInvoice?.(booking)}
                    className="p-2 rounded-lg border border-gray-200 text-blue-900 hover:bg-gray-50 hover:border-gray-300 transition flex items-center justify-center"
                   
                  >
                    <FaDownload size={18} />
                  </button>

                </div>
              </div>             

              {/* Services Summary */}
              <div className="space-y-1 flex-grow mb-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  Booked Services
                </p>

                {/* Render visible items */}
                {visibleItems?.map((item, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center px-3 py-1 rounded-xl bg-gray-50 hover:bg-white border border-transparent hover:border-orange-100 transition-all shadow-sm hover:shadow-md"
                  >
                    <div className="min-w-0 flex-1 space-y-[2px]">
                      <div className="flex items-center gap-1 flex-wrap leading-tight ">
                      <span className="font-bold text-gray-800 text-sm truncate uppercase tracking-tight">
                        {truncateString(item.service_name)} ({item.service_category_name})
                      </span>
                      <div className="flex items-center gap-1 text-[12px] text-gray-500">
                        <FaPaw className="text-orange-600 text-[10px]" />
                        <span>{item.pet_name}</span>
                        {item.pet_type && (
                          <span className="text-gray-600">({item.pet_type})</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[12px] text-gray-600 flex-wrap leading-tight">
                      <div className="flex items-center gap-1">
                        <FaCalendarAlt size={12} className="text-orange-400" />
                        <span>
                          {formatDateDisplay(item)}
                        </span>             
                      </div>
                        {hasTimeInfoDisplay(item) && (
                          <>
                            <span className="text-gray-600 text-[12px] leading-none">•</span>
                            <div className="flex items-center gap-1">
                              {item.type !== "boarding" && (item.start_time || item.time_range) && (
                                <span>{item.start_time || item.time_range}</span>
                              )}
                              {item.type !== "boarding" && (item.start_time || item.time_range) && formatDurationDisplay(item) && (
                                <span className="text-gray-600 text-[12px] leading-none">•</span>
                              )}
                              {formatDurationDisplay(item) && <span>{formatDurationDisplay(item)}</span>}
                            </div>
                          </>
                        )}
                      </div>
                         
                      {/* Show price if individual item and additional amount if exists */}
                      <span className="text-[11px] font-bold text-gray-700">
                         ₹{formatAmount(item.total_price)}
                      </span>
                     {/* {item.additional_amount > 0 && (
                        <p className="text-[11px] text-orange-600 font-medium ">
                          + ₹{formatAmount(item.additional_amount)} extra charges
                        </p>
                     )}*/}
                    </div>

                    <span
                      className={`text-[9px] font-black px-2 py-1 rounded-lg border ${getStatusBadge(
                        item.status
                      )}`}
                    >
                      {displayStatus(item.status)}
                    </span>
                  </div>
                ))}

                {/* Expandable "+ X more" button */}
                {hasMoreItems && !isExpanded && (
                  <button
                    onClick={() => toggleExpand(bookingId)}
                    className="w-full text-center py-3 rounded-xl bg-gray-100 hover:bg-gray-200 border border-dashed border-gray-300 text-gray-600 text-xs font-bold uppercase tracking-wide transition-all flex items-center justify-center gap-2"
                  >
                    <FaChevronDown className="text-gray-500" />+{" "}
                    {booking.items.length - 3} more service
                    {booking.items.length - 3 > 1 ? "s" : ""}
                  </button>
                )}

                {/* Collapse button when expanded */}
                {isExpanded && hasMoreItems && (
                  <button
                    onClick={() => toggleExpand(bookingId)}
                    className="w-full text-center py-3 rounded-xl bg-orange-50 hover:bg-orange-100 border border-dashed border-orange-300 text-orange-700 text-xs font-bold uppercase tracking-wide transition-all flex items-center justify-center gap-2"
                  >
                    <FaChevronUp className="text-orange-600" />
                    Show less
                  </button>
                )}
              </div>

              {/* Pricing & Actions */}
              <div className="pt-4 border-t border-dashed border-gray-200 mt-auto">
                <div className="flex justify-between items-end mb-4">
                  <div className="flex flex-col">
                      
                      {/* Show breakdown for partial payments */}
                    {isPartial && (
                      <span className="text-[12px] text-gray-400 mt-1">
                        Order Amount: ₹{formatAmount(grandTotal)} | Paid: ₹{formatAmount(totalPaid)}
                      </span>
                    )}

                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                      {isPartial ? "" : "Order Amount"}
                    </span>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span
                        className={`text-2xl font-black ${
                          isPaid ? "text-gray-900" : "text-rose-600"
                        }`}
                      >
                        ₹{formatAmount(isPartial ? totalRemaining : grandTotal)}
                      </span>
                      
                    </div>                    
                    
                    {booking.items?.some(item => item.additional_amount > 0) && !isPartial && (
                      <span className="text-[8px] text-gray-400 mt-1">
                        (includes extra charges)
                      </span>
                    )}
                  </div>
                  
                  {/* Payment Status Badge */}
                  {isPaid ? (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase border border-emerald-100">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                      Paid
                    </span>
                  ) : (
                    <span className="px-3 py-1.5 rounded-xl bg-rose-50 text-rose-600 text-[10px] font-black uppercase border border-rose-100">
                      Balance Due
                    </span>
                  )}
                </div>

              {/*  <div className="grid grid-cols-2 gap-3 mb-3">
                  <button
                    onClick={() => onViewInvoice?.(booking)}
                    className="py-2.5 px-2 text-[10px] font-bold uppercase rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center gap-2"
                  >
                    <FaFileInvoice className="text-gray-400" /> Invoice View
                  </button>
                  <button
                    onClick={() => onDownloadInvoice?.(booking)}
                    className="py-2.5 px-2 text-[10px] font-bold uppercase rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center gap-2"
                  >
                    <FaDownload className="text-gray-400" /> Download Invoice
                  </button>
                </div> */}

                {/* {!isPaid && (
                  <button className="w-full py-3.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-orange-100 flex items-center justify-center gap-2 active:scale-[0.98]">
                    Pay Now <FaArrowRight />
                  </button>
                )} */}
              </div>
            </div>
          </div>
        );
      })}
    </div>
   
  );
   

};
  const style = document.createElement('style');
style.textContent = `
  .preserve-3d {
    transform-style: preserve-3d;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .backface-hidden {
    backface-visibility: hidden;
  }
  .rotate-y-180 {
    transform: rotateY(180deg);
  }
`;
document.head.appendChild(style);
